import type { CombatRatings } from "@/game/model/character-stats";
import { getGameSettings } from "@/game/model/game-settings";
import type { HomePartDefinition } from "@/home/storage";

export type MonsterCombatConfig = {
  level: number;
  maxHp: number;
  xpReward: number;
  ratings: CombatRatings;
};

export function monsterCombatFromPart(part: HomePartDefinition): MonsterCombatConfig | null {
  if (part.kind !== "monster") return null;
  const d = getGameSettings();
  const level = clampInt(part.monsterLevel, 1, 100, d.monsterDefaultLevel);
  const baseHp = clampInt(part.monsterBaseHp, 1, 1_000_000, d.monsterDefaultHp);
  const baseAttack = clampInt(part.monsterBaseAttack, 1, 100_000, d.monsterDefaultAttack);
  const baseDefense = clampInt(part.monsterBaseDefense, 0, 100_000, d.monsterDefaultDefense);
  const baseDodge = clampInt(part.monsterBaseDodge, 0, 100_000, d.monsterDefaultDodge);
  const xpReward = clampInt(part.monsterXpReward, 0, 10_000_000, d.monsterDefaultXpReward);

  const scale = 1 + level * d.monsterStatScalePerLevel;
  const maxHp = Math.floor(baseHp * (1 + level * d.monsterHpPerLevelFactor));

  const ratings: CombatRatings = {
    level,
    attack: Math.floor(baseAttack * scale),
    defense: Math.floor(baseDefense * (1 + level * 0.025)),
    dodge: Math.floor(baseDodge * (1 + level * 0.02) + level * 0.3),
    hit: 40 + level * 0.5 + baseAttack * 0.2,
    critChance: Math.min(1, d.monsterDefaultCritRate + level * 0.001),
    critMultiplier: d.monsterDefaultCritMult + level * 0.002,
    attackSpeed: 0,
    moveSpeed: 0,
    hpRegenPerSec: 0,
    manaMaxBonus: 0,
  };

  return { level, maxHp, xpReward, ratings };
}

function clampInt(
  value: unknown,
  min: number,
  max: number,
  fallback: number,
): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, Math.floor(value)));
}
